import { useState } from 'react';

import { Card } from '../card/card';
import { FilterChip, FilterChipProps } from '../filter-chip/filter-chip';
import { RadioInputProps } from '../radio-button/radio-input';
import { RadioButtonGroup, RadioButtonGroupItem } from '../radio-button-group/radio-button-group';

const style = {
  container: 'flex flex-col shadow rounded overflow-hidden inline-block relative',
  popoverBase: 'flex-col max-h-104 overflow-y-auto shadow-dark py-2.5 px-3.5 w-full',
};

export interface RadioDropdownProps
  extends Omit<Partial<FilterChipProps>, 'menuContent' | 'numberSelected' | 'onClearSelection'> {
  /** The possible choices */
  data: RadioButtonGroupItem[];
  /** Callback for value changes */
  onChange: (inputs: string[], value?: string) => void;
  /** Callback for opening the Dropdown */
  onOpen?: () => void;
  /** The (HTML) name of the RadioButtonGroup */
  name: string;
  /** The actual radio value */
  radioValue?: string;
  /** The value of the FilterChip */
  value: string;
}

const formatSelection = (data: RadioButtonGroupItem[], inputs: string[], radioValue?: string) => {
  const item = data.find((item) => item.value === radioValue);
  if (!item) return radioValue;

  if (!('input1Props' in item && item.input1Props)) return item.label;

  let formatted = item.label;

  // Check for input 1
  if ((item as RadioInputProps).input1Text) formatted += ` ${(item as RadioInputProps).input1Text}`;
  if (inputs[0]) formatted += ` ${inputs[0]}`;
  else if (item.input1Props.required) return '';
  else return formatted;

  // Check for input 2
  if ((item as RadioInputProps).input2Text) formatted += ` ${(item as RadioInputProps).input2Text}`;
  if (inputs.length > 1 && inputs[1]) formatted += ` ${inputs[1]}`;
  else if (item.input2Props?.required) return '';

  return formatted;
};

// eslint-disable-next-line max-lines-per-function
export const RadioDropdown = (props: RadioDropdownProps) => {
  const {
    data: defaultData,
    value,
    onChange,
    onOpen,
    radioValue,
    name,
    width = '352px',
    isOpen = false,
    ...rest
  } = props;
  const [data, setData] = useState(defaultData);
  const [selection, setSelection] = useState<[string[], string?]>([[], radioValue]);

  const onValueChange = (radioValue: string, inputs: string[]) => {
    setSelection([inputs, radioValue]);
    const itemIndex = data.findIndex((v) => v.value === radioValue);
    const item = data[itemIndex];
    if (item && 'input1Props' in item) {
      const newItem = {
        ...item,
        input1Props: { ...item.input1Props, value: inputs[0] },
      };
      if (inputs[1]) {
        newItem.input2Props = { ...item.input2Props, value: inputs[1] };
      }
      data[itemIndex] = newItem;
      setData([...data]);
    }
    onChange(inputs, radioValue);
  };

  const onClearSelection = () => {
    setSelection([[], undefined]);
    onChange([], undefined);
  };

  const numberSelected = selection[1] === undefined ? 0 : 1;
  const firstSelection = formatSelection(data, selection[0], selection[1]);

  return (
    <FilterChip
      menuContent={
        <div style={{ width }}>
          <Card className={style.popoverBase}>
            <div role="menu">
              <RadioButtonGroup
                data={data}
                name={name}
                layout={'vertical'}
                value={selection[1]}
                onValueChange={onValueChange}
                buttonSize="medium"
              />
            </div>
          </Card>
        </div>
      }
      numberSelected={numberSelected}
      onClearSelection={onClearSelection}
      firstSelection={firstSelection}
      width={width}
      isOpen={isOpen}
      value={value}
      onOpen={onOpen}
      {...rest}
    />
  );
};

export default RadioDropdown;
